import React, { useState, useEffect } from 'react';
import './ampol-cash-recipient-form.scss';
import CardImage from '../../../../components/card-image/card-image';
import CartItemSummary from '../cart-item-summary/cart-item-summary';
import AmpolCashAddressForm from '../ampol-cash-address-form/ampol-cash-address-form';
import QuantityField from '../../../../components/quantity-field/QuantityField';
import { v4 as uuidv4 } from 'uuid';
import Validation from 'constants/validation-messages';

/**
 * Ampol Cash Recipient Form
 * @param formId Form unique id
 * @param cartItem The cart item object
 * @param recipientUuid The uuid for an existing recipient so we can edit their details
 * @param isPhysical Whether the recipient is a physical address
 * @param physicalAddressRecipient The physical address details for the recipient
 * @param onSubmit AmpolCashRecipientForm submit event
 * @param recipientsEditClick parent event
 * @param recipientsRemoveClick parent event
 * @returns JSX Element
 */
const AmpolCashRecipientForm = ({ formId, cartItem, recipientUuid, isPhysical, physicalAddressRecipient, onSubmit,
  recipientsEditClick, recipientsRemoveClick, cartItemUuid }) => {
  const [totalRemainingQuantity, setTotalRemainingQuantity] = useState(cartItem.remainingRecipients);
  const [currentRemainingQuantity, setCurrentRemainingQuantity] = useState(cartItem.remainingRecipients);
  const [cardQuantity, setCardQuantity] = useState(isPhysical ? cartItem.quantity : 1);
  const [qtyFieldMsg, setQtyFieldMsg] = useState('');

  const [recipient, setRecipient] = useState(null);
  const [recipientNumber, setRecipientNumber] = useState(cartItem.recipients ? cartItem.recipients.length + 1 : 1);

  const [isAddressFormValid, setIsAddressFormValid] = useState(false);
  const [isRecipientFormValid, setIsRecipientFormValid] = useState(false);
  const [itemSummaryCartItem, setItemSummaryCartItem] = useState(cartItem);

  useEffect(() => {
    validateQuantity(cardQuantity);
  }, [cardQuantity]);

  useEffect(() => {
    if (!recipientUuid) {
      document.getElementById(formId).reset();
    }
  }, [recipientUuid, cartItemUuid]);

  useEffect(() => {
    if (!isPhysical) {
      setItemSummaryCartItem({
        ...cartItem,
        recipients: cartItem.recipients.filter(r => r.uuid !== recipientUuid)
      });

      if (!recipientUuid) {
        setRecipient(null);
        setRecipientNumber(cartItem.recipients ? cartItem.recipients.length + 1 : 1);
        setCardQuantity(1);
        setTotalRemainingQuantity(cartItem.remainingRecipients);
        return;
      }

      const selectedRecipient = cartItem.recipients.find(r => r.uuid === recipientUuid);
      const recipientIndex = cartItem.recipients.findIndex(r => r.uuid === recipientUuid);

      setRecipient(selectedRecipient);
      setRecipientNumber(recipientIndex + 1);
      setCardQuantity(selectedRecipient.quantity);
      setTotalRemainingQuantity(cartItem.remainingRecipients + selectedRecipient.quantity);
    } else {
      setRecipient(physicalAddressRecipient);
      setCardQuantity(cartItem.quantity);
      setItemSummaryCartItem(cartItem);
    }
  }, [recipientUuid, cartItem, physicalAddressRecipient]);

  useEffect(() => {
    setCurrentRemainingQuantity(totalRemainingQuantity - cardQuantity);
    setIsRecipientFormValid(isAddressFormValid && cardQuantity > 0 && cardQuantity % 1 === 0);
  }, [totalRemainingQuantity, cardQuantity, isAddressFormValid]);

  const onAddressFormSubmit = (recipient) => {
    if (!isRecipientFormValid) {
      validateQuantity();
      return;
    }

    const values = {
      recipient : {
        ...recipient,
        uuid : recipientUuid ? recipientUuid : uuidv4(),
        quantity: cardQuantity,
      },
      isPhysical: isPhysical,
      hasRemainingRecipients: currentRemainingQuantity > 0,
    };

    document.getElementById(formId).reset();

    onSubmit(values);
  };

  const onAddressFormStateChange = (isValid) => {
    setIsAddressFormValid(isValid);
    setIsRecipientFormValid(isValid && cardQuantity > 0 && cardQuantity % 1 === 0);
  };

  const validateQuantity = () => {
    if (cardQuantity === '') {
      setQtyFieldMsg(Validation.QUANTITY_MSG_REQUIRED);
    }
    else if (cardQuantity % 1 !== 0) {
      setQtyFieldMsg(Validation.INTEGER);
    }
    else {
      setQtyFieldMsg('');
    }
  };

  return <>
    <div className="ampol-cash-recipient-form">
      <div className="ampol-cash-recipient-form__card-details-container">
        <div className="ampol-cash-recipient-form__grid-cell ampol-cash-recipient-form__card-image">
          <CardImage
            isLarge={true}
            isDigital={cartItem.mediaType === 'D'}
            imageSource={cartItem?.plasticTypeSource}
            useCacheImage={true}
          />
        </div>
        <div className="ampol-cash-recipient-form__grid-cell">
          <CartItemSummary
            cartItem={itemSummaryCartItem}
            inlineView={true}
            showImage={false}
            showQuantity={true}
            showRecipients={!isPhysical}
            recipientsEditClick={recipientsEditClick}
            recipientsRemoveClick={recipientsRemoveClick}
          />
        </div>
      </div>

      <div className="ampol-cash-recipient-form__bold-text">
        {isPhysical
          ? <span>All physical cards in your order will be sent to this recipient</span>
          : <span>Recipient {recipientNumber}</span>
        }
      </div>

      <AmpolCashAddressForm
        formId={formId}
        shippingAddress={recipient}
        onSubmit={onAddressFormSubmit}
        onStateChange={onAddressFormStateChange}
        formType={isPhysical ? 'shipping' : 'digital'}
      />

      {!isPhysical &&
        <div className="ampol-cash-recipient-form__cards-remaining-container">
          <div className="ampol-cash-recipient-form__grid-cell">
            <div>
              How many cards will be sent to this address?
            </div>
            <div className="ampol-cash-recipient-form__bold-text">
              ({currentRemainingQuantity} card{currentRemainingQuantity === 1 ? '' : 's'} remaining)
            </div>
          </div>
          <div className='ampol-cash-recipient-form__quantity-field'>
            <QuantityField
              max={totalRemainingQuantity}
              quantity={cardQuantity}
              setQuantity={setCardQuantity}
              quantityMessage={qtyFieldMsg}
            />
          </div>
        </div>
      }
    </div>
  </>;
};

export default AmpolCashRecipientForm;
